// Адмінка → «Контент» (ТЗ 39 п.7.4 / п.8): правка вмісту ІСНУЮЧИХ сторінок
// публічного сайту. Сторінки звідси не додаються — перелік фіксований у
// lib/siteContent.js. Порожнє поле = текст за замовчуванням із коду.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import {
  BLOCK_DEFAULTS, CALC_DEFAULTS, CALC_HELP, SITE_PAGES, loadSiteContent, sanitizeHtml,
} from './trading/public-site/lib/siteContent';
import { TEXT_DEFAULTS, TEXT_PAGES } from './trading/public-site/lib/siteTexts';

const C = {
  card: { background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: 20, marginBottom: 16 },
  title: { fontSize: 16, fontWeight: 600, color: '#101828', marginBottom: 4 },
  sub: { fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.5 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 8,
           fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' },
  count: (over) => ({ fontSize: 11, color: over ? '#B91C1C' : '#9CA3AF', textAlign: 'right', marginTop: 2 }),
  page: { padding: '14px 0', borderTop: '1px solid #F3F4F6' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  btn: (primary) => ({ padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                       fontWeight: 600, background: primary ? '#163382' : '#F3F4F6', color: primary ? '#fff' : '#374151' }),
  tool: { padding: '4px 9px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff',
          cursor: 'pointer', fontSize: 13, minWidth: 30 },
  editor: { minHeight: 110, padding: '10px 12px', border: '1px solid #E5E7EB', borderTop: 'none',
            borderRadius: '0 0 8px 8px', fontSize: 14, lineHeight: 1.55, color: '#1F2937', outline: 'none' },
};

const TITLE_MAX = 200;
const DESC_MAX = 500;

/* Простий WYSIWYG на contentEditable — без нових залежностей. Некерований:
   HTML читається з DOM на кожну зміну; зовнішнє значення застосовується лише
   при зміні `resetKey` (завантаження, «повернути текст за замовчуванням»). */
function RichEditor({ value, onChange, resetKey }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = sanitizeHtml(value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
  const emit = () => onChange(ref.current ? ref.current.innerHTML : '');
  const cmd = (name, arg) => {
    ref.current?.focus();
    document.execCommand(name, false, arg);
    emit();
  };
  const link = () => {
    const url = window.prompt('Посилання (https://… або /сторінка):', 'https://');
    if (url) cmd('createLink', url);
  };
  const tools = [
    ['B', () => cmd('bold'), 'Жирний', { fontWeight: 700 }],
    ['I', () => cmd('italic'), 'Курсив', { fontStyle: 'italic' }],
    ['U', () => cmd('underline'), 'Підкреслений', { textDecoration: 'underline' }],
    ['• Список', () => cmd('insertUnorderedList'), 'Маркований список'],
    ['1. Список', () => cmd('insertOrderedList'), 'Нумерований список'],
    ['¶ Абзац', () => cmd('formatBlock', 'P'), 'Звичайний абзац'],
    ['🔗', link, 'Посилання'],
    ['⨯ формат', () => { cmd('removeFormat'); cmd('unlink'); }, 'Прибрати форматування'],
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: 6, background: '#F9FAFB',
                    border: '1px solid #E5E7EB', borderRadius: '8px 8px 0 0' }}>
        {tools.map(([t, fn, title, st]) => (
          <button key={t} type="button" title={title} style={{ ...C.tool, ...st }}
            onMouseDown={(e) => { e.preventDefault(); fn(); }}>{t}</button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning style={C.editor}
        onInput={emit} onBlur={emit} className="admin-rich-editor" />
    </div>
  );
}


/* ── Блоки з динамічним текстом ─────────────────────────────────────────────
   Опис форм: поле → тип. Перелік полів має збігатися зі схемою бекенду
   (routers/site_content.py → _BLOCK_SCHEMA). Типи: text | area | bool | num;
   list — список однакових елементів із додаванням/видаленням/порядком. */
const PAIR_FIELDS = [
  { k: 'title', label: 'Заголовок', type: 'text' },
  { k: 'text', label: 'Текст', type: 'area' },
];
const BLOCK_FORMS = [
  { key: 'general', title: 'Заявлена дохідність', where: 'Число «45%+» на всіх сторінках: заголовок головної, великі цифри «Дохідності» й «Переваг», футер і тексти з {returns_claim} / {returns_num}',
    fields: [
      { k: 'returns_claim', label: 'Заявлена дохідність', type: 'text',
        hint: 'Головна: «…із доходністю X річних». У футері підставляється замість {returns_claim}.' },
    ] },
  { key: 'contacts', title: 'Адреса й контакти', where: 'Сторінка «Контакти»',
    fields: [
      { k: 'address', label: 'Адреса', type: 'text' },
      { k: 'office_note', label: 'Примітка під адресою', type: 'area', hint: 'Порожньо — примітку не показувати.' },
      { k: 'phone', label: 'Телефон', type: 'text', hint: 'Посилання для дзвінка складається з цифр автоматично. Порожньо — рядок сховано.' },
      { k: 'telegram', label: 'Telegram', type: 'text', hint: '@нік або повне посилання https://t.me/…' },
      { k: 'email', label: 'Email', type: 'text' },
    ] },
  { key: 'cases', title: 'Кейси клієнтів (відгуки): заголовок і самі кейси', where: 'Головна, блок «Кейси клієнтів»',
    fields: [
      { k: 'visible', label: 'Показувати блок на сайті', type: 'bool' },
      { k: 'badge', label: 'Мітка над заголовком', type: 'text' },
      { k: 'title', label: 'Заголовок', type: 'area', hint: 'Новий рядок = перенос на сайті.' },
      { k: 'lead', label: 'Підзаголовок', type: 'area' },
    ],
    lists: [{ k: 'items', label: 'Кейс', max: 30, fields: [
      { k: 'num', label: 'Номер кейсу', type: 'text', half: true },
      { k: 'title', label: 'Назва', type: 'text', half: true },
      { k: 'pct', label: 'Зелена мітка (%)', type: 'text', half: true },
      { k: 'profit', label: 'Чистий прибуток', type: 'text', half: true },
      { k: 'start', label: 'Старт', type: 'text', half: true },
      { k: 'period', label: 'Період', type: 'text', half: true },
      { k: 'name', label: 'Ім’я клієнта', type: 'text', half: true },
      { k: 'status', label: 'Статус під іменем', type: 'text', half: true },
      { k: 'quote', label: 'Відгук', type: 'area' },
      { k: 'date', label: 'Дати (внизу картки)', type: 'text' },
    ] }] },
  { key: 'faq', title: 'FAQ: питання й відповіді', where: 'Головна, блок «FAQ»',
    lists: [{ k: 'items', label: 'Питання', max: 40, fields: [
      { k: 'q', label: 'Питання', type: 'text' },
      { k: 'a', label: 'Відповідь', type: 'area', hint: 'Новий рядок = перенос на сайті.' },
    ] }] },
  { key: 'footer', title: 'Футер: слоган, попередження, посилання', where: 'Низ кожної сторінки сайту',
    fields: [
      { k: 'tagline', label: 'Слоган під логотипом', type: 'area' },
      { k: 'disclaimer_title', label: 'Заголовок попередження', type: 'text' },
      { k: 'disclaimer', label: 'Попередження про ризики', type: 'area', hint: '{returns_claim} — заявлена дохідність із блоку «Загальне».' },
      { k: 'copyright', label: 'Рядок копірайту', type: 'text' },
    ],
    lists: [{ k: 'legal', label: 'Посилання внизу', max: 10, fields: [
      { k: 'label', label: 'Текст', type: 'text', half: true },
      { k: 'href', label: 'Адреса', type: 'text', half: true, hint: 'https://…, mailto:… або /сторінка. Порожньо — посилання нікуди не веде.' },
    ] }] },
  { key: 'pricing', title: 'Списки сторінки «Ціни»: картки, тарифи, оплата, гарантія', where: 'Тарифи — це й ставки калькулятора',
    fields: [
      { k: 'tiers_note', label: 'Примітка під діаграмою тарифів', type: 'area' },
    ],
    lists: [
      { k: 'free', label: 'Картка «Безкоштовні умови»', max: 6, fields: [
        { k: 'prefix', label: 'Перед числом', type: 'text', half: true },
        { k: 'value', label: 'Число', type: 'num', half: true },
        { k: 'unit', label: 'Після числа', type: 'text', half: true },
        { k: 'title', label: 'Заголовок', type: 'text', half: true },
        { k: 'text', label: 'Текст', type: 'area' },
      ] },
      { k: 'tiers', label: 'Тариф', max: 12, fields: [
        { k: 'range', label: 'Сума на рахунку', type: 'text', half: true },
        { k: 'pct', label: 'Комісія, %', type: 'num', half: true },
        { k: 'from_usd', label: 'Від суми, $ (для калькулятора)', type: 'num', half: true,
          hint: 'Калькулятор бере ставку тарифу, у якого «від суми» найбільша, але не більша за суму на початку сезону.' },
        { k: 'top', label: 'Позначка «Можлива знижка»', type: 'bool' },
      ] },
      { k: 'billing', label: 'Як відбувається оплата', max: 8, fields: PAIR_FIELDS },
      { k: 'guarantee', label: 'Факт гарантії', max: 8, fields: PAIR_FIELDS },
    ] },
];

const clone = (v) => JSON.parse(JSON.stringify(v));

// Чи є на вкладці незбережені зміни — для Admin.js (перемикання вкладок
// адмінки). Модульна змінна, бо вкладка при перемиканні демонтується.
let unsavedNow = false;
export const contentTabHasUnsaved = () => unsavedNow;
const LEAVE_MSG = 'На вкладці «Контент» є незбережені зміни. Вийти без збереження?';
export const confirmLeaveContentTab = () => !unsavedNow || window.confirm(LEAVE_MSG);

const normHelpHtml = (h) => sanitizeHtml(h || '').replace(/\s+/g, ' ').trim();

// Значення форми → те, що піде на бекенд (числа — числами, текст — обрізаний).
function normBlock(form, v) {
  const normFields = (fields, obj) => Object.fromEntries((fields || []).map((f) => {
    const x = obj?.[f.k];
    if (f.type === 'bool') return [f.k, !!x];
    if (f.type === 'num') return [f.k, x === '' || x == null ? 0 : Number(x)];
    return [f.k, String(x ?? '').replace(/\r\n/g, '\n').trim()];
  }));
  const out = normFields(form.fields, v);
  (form.lists || []).forEach((l) => {
    out[l.k] = (v?.[l.k] || []).map((it) => normFields(l.fields, it));
  });
  return out;
}

function Field({ f, value, onChange }) {
  if (f.type === 'bool') {
    return (
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#374151', margin: '4px 0 10px' }}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {f.label}
      </label>
    );
  }
  return (
    <div style={{ marginBottom: 10, gridColumn: f.half ? undefined : '1 / -1' }}>
      <label style={C.label}>{f.label}</label>
      {f.type === 'area'
        ? <textarea style={{ ...C.input, resize: 'vertical' }} rows={3} value={value ?? ''}
            onChange={(e) => onChange(e.target.value)} />
        : <input style={C.input} type={f.type === 'num' ? 'number' : 'text'} step="any" value={value ?? ''}
            onChange={(e) => onChange(e.target.value)} />}
      {f.hint && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{f.hint}</div>}
    </div>
  );
}

function ListEditor({ list, items, onChange }) {
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const blank = () => Object.fromEntries(list.fields.map((f) =>
    [f.k, f.type === 'bool' ? false : f.type === 'num' ? 0 : '']));
  return (
    <div style={{ marginTop: 8 }}>
      {items.map((it, i) => (
        <div key={i} style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 12, marginBottom: 10, background: '#FCFCFD' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <strong style={{ fontSize: 13, flex: 1 }}>{list.label} {i + 1}</strong>
            <button type="button" style={C.tool} title="Вище" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button type="button" style={C.tool} title="Нижче" disabled={i === items.length - 1} onClick={() => move(i, 1)}>↓</button>
            <button type="button" style={{ ...C.tool, color: '#B91C1C' }} title="Видалити"
              onClick={() => { if (window.confirm(`Видалити «${list.label} ${i + 1}»?`)) onChange(items.filter((_, j) => j !== i)); }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', columnGap: 12 }}>
            {list.fields.map((f) => (
              <Field key={f.k} f={f} value={it[f.k]}
                onChange={(v) => onChange(items.map((x, j) => (j === i ? { ...x, [f.k]: v } : x)))} />
            ))}
          </div>
        </div>
      ))}
      {items.length < list.max && (
        <button type="button" style={C.btn(false)} onClick={() => onChange([...items, blank()])}>
          + Додати: {list.label.toLowerCase()}
        </button>
      )}
    </div>
  );
}

const pill = (bg, color) => ({ marginLeft: 8, fontSize: 11, fontWeight: 600, color, background: bg,
  borderRadius: 6, padding: '2px 6px', verticalAlign: 'middle' });
const UnsavedPill = () => <span style={pill('#FFF7ED', '#C2410C')}>● не збережено</span>;

function BlockCard({ form, value, overridden, unsaved, onChange, onReset }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ ...C.card, padding: '12px 14px', marginBottom: 12, background: '#F8FAFF',
                  borderColor: unsaved ? '#FDBA74' : '#DBE4FF' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <div style={{ flex: 1 }}>
          <div style={{ ...C.title, marginBottom: 2 }}>
            {open ? '▾' : '▸'} {form.title}
            {overridden && <span style={pill('#EEF2FF', '#163382')} title="Текст відрізняється від початкового">змінено</span>}
            {unsaved && <UnsavedPill />}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{form.where}{open ? '' : ' · натисніть, щоб розгорнути'}</div>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', columnGap: 12 }}>
            {(form.fields || []).map((f) => (
              <Field key={f.k} f={f} value={value[f.k]} onChange={(v) => onChange({ ...value, [f.k]: v })} />
            ))}
          </div>
          {(form.lists || []).map((l) => (
            <div key={l.k} style={{ marginTop: 6 }}>
              {(form.lists.length > 1 || form.fields) && <div style={{ ...C.label, fontSize: 13, marginTop: 8 }}>{l.label}: список</div>}
              <ListEditor list={l} items={value[l.k] || []} onChange={(items) => onChange({ ...value, [l.k]: items })} />
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <button type="button" style={{ ...C.tool, fontSize: 12 }}
              onClick={() => { if (window.confirm(`Повернути блок «${form.title}» до тексту за замовчуванням?`)) onReset(); }}>
              Повернути весь блок за замовчуванням
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Тексти сторінок (етап 2) ──────────────────────────────────────────────
   Поля будуються з реєстру TEXT_PAGES (lib/siteTexts*.js). У полі — чинний
   текст; зберігаються лише ті, що відрізняються від тексту за замовчуванням. */
const normText = (v) => String(v ?? '').replace(/\r\n/g, '\n').trim();
const publicSiteUrl = (path) => {
  const host = window.location.hostname.replace(/^app\./, '');
  return `${window.location.protocol}//${host}${path}`;
};

function TextField({ fkey, label, value, unsaved, onChange }) {
  const def = TEXT_DEFAULTS[fkey];
  const changed = normText(value) !== normText(def);
  const rows = Math.min(8, Math.max(1, Math.ceil(String(value || '').length / 90) + (String(value || '').match(/\n/g) || []).length));
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <label style={{ ...C.label, marginBottom: 0, flex: 1 }}>
          {label}
          {changed && <span style={pill('#EEF2FF', '#163382')}>змінено</span>}
          {unsaved && <UnsavedPill />}
        </label>
        {changed && (
          <button type="button" style={{ ...C.tool, fontSize: 11 }} title="Повернути текст за замовчуванням"
            onClick={() => onChange(def)}>↺ як було</button>
        )}
      </div>
      <textarea style={{ ...C.input, resize: 'vertical', borderColor: unsaved ? '#FDBA74' : '#E5E7EB' }}
        rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      {changed && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3, whiteSpace: 'pre-wrap' }}>
          За замовчуванням: {def}
        </div>
      )}
    </div>
  );
}

/* Де на сторінці стоїть кожен блок зі списками: сторінка реєстру → [блок,
   секція, ПІСЛЯ якої він іде ('' — на початку сторінки)]. Так усе, що є на
   сторінці, редагується на її вкладці, у тому порядку, як на сайті. */
const PAGE_BLOCKS = {
  common:   [['general', ''], ['footer', 'common.nav']],
  home:     [['cases', 'home.ibkr'], ['faq', 'home.cases']],
  pricing:  [['pricing', 'pricing.sections']],
  contacts: [['contacts', 'contacts.page']],
};

function PageTexts({ texts, baseline, onChange, renderBlock, blockUnsaved, blockText }) {
  const [pageId, setPageId] = useState(TEXT_PAGES[0].id);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const pageUnsaved = (pg) => (PAGE_BLOCKS[pg.id] || []).some(([b]) => blockUnsaved(b))
    || pg.sections.some(sec => sec.fields.some(([k]) => {
      const key = `${sec.id}.${k}`;
      return baseline && normText(texts[key]) !== normText(baseline[key]);
    }));
  // Пошук іде по всіх сторінках: підпис поля, назва секції, сам текст і вміст
  // блоків (адреса, кейси, питання FAQ…).
  const blocksOf = (pg) => (PAGE_BLOCKS[pg.id] || []).filter(([b]) => !q || blockText(b).includes(q));
  const shown = q
    ? TEXT_PAGES.map(pg => ({ ...pg, sections: pg.sections.map(sec => ({ ...sec,
        fields: sec.fields.filter(([k, label]) => `${label} ${sec.label} ${texts[`${sec.id}.${k}`] || ''}`.toLowerCase().includes(q)),
      })).filter(sec => sec.fields.length) })).filter(pg => pg.sections.length || blocksOf(pg).length)
    : TEXT_PAGES.filter(pg => pg.id === pageId);
  // Блок стоїть після своєї секції; якщо пошук її сховав — на початку сторінки.
  const blocksAfter = (pg, secId) => {
    const visible = new Set(pg.sections.map(sec => sec.id));
    return blocksOf(pg)
      .filter(([, after]) => (secId === '' ? (after === '' || !visible.has(after)) : after === secId))
      .map(([b]) => <div key={`blk-${b}`}>{renderBlock(b)}</div>);
  };
  return (
    <div style={C.card}>
      <div style={C.title}>Сторінки сайту</div>
      <div style={C.sub}>
        Усі тексти сайту, по сторінках і в тому порядку, як на сайті: заголовки, описи, пункти, кнопки,
        а також списки (кейси, FAQ, тарифи, контакти) — їх можна доповнювати, видаляти й переставляти.
        Оберіть сторінку або знайдіть текст пошуком.
        Зміни з'являються на сайті після кнопки «Зберегти» внизу (для відвідувача — після оновлення сторінки).
        Розмітка: <code>*слово*</code> — кольорове виділення (де воно є в дизайні), <code>**слово**</code> — жирний,
        {' '}<code>[текст](/сторінка)</code> або <code>[текст](https://…)</code> — посилання, новий рядок — перенос.
        Змінні: <code>{'{returns_claim}'}</code> (заявлена дохідність), <code>{'{returns_num}'}</code> (лише її число), <code>{'{fee_pct}'}</code> (діапазон ставок),
        {' '}<code>{'{free_months}'}</code>, <code>{'{threshold}'}</code>,
        {' '}<code>{'{tax_pct}'}</code>, <code>{'{deposit_fee_pct}'}</code>. Поле, у якому текст не змінювали, бере текст із коду сайту.
      </div>
      <input style={{ ...C.input, marginBottom: 12 }} placeholder="Пошук тексту на всіх сторінках…"
        value={query} onChange={(e) => setQuery(e.target.value)} />
      {!q && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {TEXT_PAGES.map(pg => (
            <button key={pg.id} type="button" onClick={() => setPageId(pg.id)}
              style={{ ...C.btn(pg.id === pageId), padding: '6px 12px', fontWeight: 500 }}>
              {pg.label}{pageUnsaved(pg) ? ' ●' : ''}
            </button>
          ))}
        </div>
      )}
      {q && !shown.length && <div style={{ color: '#6B7280', fontSize: 13 }}>Нічого не знайдено.</div>}
      {shown.map(pg => (
        <div key={pg.id}>
          {(q || pg.path) && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '6px 0 8px' }}>
              <strong style={{ fontSize: 15 }}>{pg.label}</strong>
              {pg.path && <a href={publicSiteUrl(pg.path)} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: '#1c69e3' }}>відкрити сторінку ↗</a>}
            </div>
          )}
          {blocksAfter(pg, '')}
          {pg.sections.map(sec => (<React.Fragment key={sec.id}>
            <div style={{ border: '1px solid #F3F4F6', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#101828' }}>{sec.label}</div>
              {sec.fields.map(([k, label]) => {
                const key = `${sec.id}.${k}`;
                return (
                  <TextField key={key} fkey={key} label={label} value={texts[key]}
                    unsaved={!!baseline && normText(texts[key]) !== normText(baseline[key])}
                    onChange={(v) => onChange(key, v)} />
                );
              })}
            </div>
            {blocksAfter(pg, sec.id)}
          </React.Fragment>))}
        </div>
      ))}
    </div>
  );
}

export default function ContentTab() {
  const [pages, setPages] = useState({});
  const [calc, setCalc] = useState({ ...CALC_DEFAULTS });
  const [help, setHelp] = useState({});
  const [blocks, setBlocks] = useState(() => clone(BLOCK_DEFAULTS));
  const [texts, setTexts] = useState(() => ({ ...TEXT_DEFAULTS }));
  const [textsBaseline, setTextsBaseline] = useState(null);
  const [resetKeys, setResetKeys] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Знімок збереженого стану (після завантаження / збереження) — з ним
  // порівнюється форма, щоб показати незбережене.
  const [baseline, setBaseline] = useState(null);
  const baselinePending = useRef(false);
  const [applyN, setApplyN] = useState(0); // кожне apply → новий знімок, навіть якщо форма та сама

  const apply = useCallback((d) => {
    baselinePending.current = true;
    setApplyN(n => n + 1);
    setPages(d.pages || {});
    const c = d.calculator || {};
    setCalc(Object.fromEntries(Object.keys(CALC_DEFAULTS).map(k => [k, c[k] ?? CALC_DEFAULTS[k]])));
    // У редакторі — перекриття або текст за замовчуванням, щоб правити, а не писати з нуля.
    setHelp(Object.fromEntries(CALC_HELP.map(h => [h.key, c.help?.[h.key] || h.html])));
    setResetKeys(k => Object.fromEntries(CALC_HELP.map(h => [h.key, (k[h.key] || 0) + 1])));
    const b = d.blocks || {};
    setBlocks(Object.fromEntries(BLOCK_FORMS.map(f =>
      [f.key, clone({ ...BLOCK_DEFAULTS[f.key], ...(b[f.key] || {}) })])));
    // У полях — збережений текст або текст за замовчуванням (ключі поза реєстром ігноруються).
    const tx = { ...TEXT_DEFAULTS };
    Object.entries(d.texts || {}).forEach(([k, v]) => { if (k in TEXT_DEFAULTS && typeof v === 'string') tx[k] = v; });
    setTexts(tx);
    setTextsBaseline(tx);
  }, []);

  useEffect(() => {
    api.get('/api/admin/site-content')
      .then(r => { apply(r.data); setLoaded(true); })
      .catch(e => setError(e.response?.data?.detail || 'Не вдалося завантажити контент'));
  }, [apply]);

  const snap = {
    pages: JSON.stringify(Object.keys(pages).sort().map(k =>
      [k, (pages[k]?.title || '').trim(), (pages[k]?.description || '').trim()])
      .filter(([, t, d]) => t || d)),
    calc: JSON.stringify(Object.keys(CALC_DEFAULTS).map(k => Number(calc[k]))),
    help: JSON.stringify(CALC_HELP.map(h => normHelpHtml(help[h.key]))),
    ...Object.fromEntries(BLOCK_FORMS.map(f => [`b:${f.key}`, JSON.stringify(normBlock(f, blocks[f.key]))])),
    ...Object.fromEntries(TEXT_PAGES.map(pg => [`t:${pg.id}`, JSON.stringify(pg.sections.flatMap(sec =>
      sec.fields.map(([k]) => normText(texts[`${sec.id}.${k}`]))))])),
  };
  const snapKey = JSON.stringify(snap);
  useEffect(() => {
    if (loaded && baselinePending.current) {
      baselinePending.current = false;
      setBaseline(JSON.parse(snapKey));
    }
  }, [loaded, snapKey, applyN]);
  const changedParts = baseline ? Object.keys(snap).filter(k => snap[k] !== baseline[k]) : [];
  const isUnsaved = (part) => changedParts.includes(part);
  const dirty = changedParts.length > 0;
  const PART_LABEL = {
    pages: 'title/description сторінок', calc: 'калькулятор', help: 'підказки «?»',
    ...Object.fromEntries(BLOCK_FORMS.map(f => [`b:${f.key}`, f.title])),
    ...Object.fromEntries(TEXT_PAGES.map(pg => [`t:${pg.id}`, `тексти: ${pg.label}`])),
  };

  // Попередження при виході: закриття/оновлення сторінки і посилання сайту
  // (меню кабінету). Перемикання вкладок адмінки — у Admin.js.
  useEffect(() => {
    unsavedNow = dirty;
    if (!dirty) return undefined;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.getAttribute('href').startsWith('#')) return;
      if (!window.confirm(LEAVE_MSG)) { e.preventDefault(); e.stopPropagation(); }
      else unsavedNow = false;
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty]);
  useEffect(() => () => { unsavedNow = false; }, []);

  const setPage = (path, field, v) => {
    setSaved(false);
    setPages(p => ({ ...p, [path]: { ...(p[path] || {}), [field]: v } }));
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const norm = normHelpHtml;
      const body = {
        pages: Object.fromEntries(Object.entries(pages).map(([k, v]) =>
          [k, { title: v.title || '', description: v.description || '' }])),
        calculator: {
          ...Object.fromEntries(Object.keys(CALC_DEFAULTS).map(k => [k, Number(calc[k])])),
          // Текст, що збігається з дефолтом, не зберігаємо — тоді правка дефолту
          // в коді дійде до сайту.
          help: Object.fromEntries(CALC_HELP.map(h => {
            const cur = sanitizeHtml(help[h.key] || '');
            return [h.key, norm(cur) === norm(h.html) ? '' : cur];
          })),
        },
      };
      // Блок, що збігається з дефолтом, не зберігаємо (null) — тоді правка
      // дефолту в коді дійде до сайту.
      body.blocks = Object.fromEntries(BLOCK_FORMS.map(f => {
        const cur = normBlock(f, blocks[f.key]);
        const def = normBlock(f, BLOCK_DEFAULTS[f.key]);
        return [f.key, JSON.stringify(cur) === JSON.stringify(def) ? null : cur];
      }));
      // Тексти сторінок: лише ті, що відрізняються від тексту за замовчуванням
      // (порожній теж — так поле ховається на сайті).
      body.texts = Object.fromEntries(Object.keys(TEXT_DEFAULTS)
        .filter(k => normText(texts[k]) !== normText(TEXT_DEFAULTS[k]))
        .map(k => [k, normText(texts[k])]));
      const r = await api.put('/api/admin/site-content', body);
      apply(r.data);
      loadSiteContent(true);
      setSaved(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не вдалося зберегти');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div style={{ color: error ? '#EF4444' : '#6B7280', padding: 20 }}>{error || 'Завантаження…'}</div>;

  const num = (k, label, hint, step = 'any') => (
    <div>
      <label style={C.label}>{label}</label>
      <input type="number" min="0" step={step} style={C.input} value={calc[k]}
        onChange={e => { setSaved(false); setCalc(c => ({ ...c, [k]: e.target.value })); }} />
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{hint}</div>
    </div>
  );

  return (
    <div>
      <style>{`.admin-rich-editor ol{list-style:decimal;padding-left:22px;margin:6px 0}
.admin-rich-editor ul{list-style:disc;padding-left:22px;margin:6px 0}
.admin-rich-editor p{margin:0 0 6px}.admin-rich-editor a{color:#1c69e3;text-decoration:underline}`}</style>
      <PageTexts texts={texts} baseline={textsBaseline}
        onChange={(k, v) => { setSaved(false); setTexts(t => ({ ...t, [k]: v })); }}
        blockUnsaved={(key) => isUnsaved(`b:${key}`)}
        blockText={(key) => JSON.stringify(blocks[key] || {}).toLowerCase()}
        renderBlock={(key) => {
          const f = BLOCK_FORMS.find(x => x.key === key);
          return (
            <BlockCard form={f} value={blocks[key] || {}}
              overridden={JSON.stringify(normBlock(f, blocks[key])) !== JSON.stringify(normBlock(f, BLOCK_DEFAULTS[key]))}
              unsaved={isUnsaved(`b:${key}`)}
              onChange={v => { setSaved(false); setBlocks(b => ({ ...b, [key]: v })); }}
              onReset={() => { setSaved(false); setBlocks(b => ({ ...b, [key]: clone(BLOCK_DEFAULTS[key]) })); }} />
          );
        }} />

      <div style={C.card}>
        <div style={C.title}>Калькулятор на головній (trading.com.ua){isUnsaved('calc') && <UnsavedPill />}</div>
        <div style={C.sub}>
          Витрати, які калькулятор віднімає від результату бектесту. Комісія Trading.com.ua
          знімається 1 березня, 1 червня, 1 вересня і 1 грудня з приросту рахунку за сезон;
          ставка — з тарифів блоку «Ціни» вище (за сумою на початку сезону); збиток сезону
          спершу відіграється, і лише приріст понад нього тарифікується.
          Стратегія для розрахунку — завжди активна (вкладка «Стратегія»).
        </div>
        <div style={C.grid}>
          {num('free_months', 'Безкоштовних місяців на старті', 'від дати початку розрахунку комісія Trading не береться', '1')}
          {num('trading_fee_threshold', 'Комісія рахується від суми, $', 'сезон, що почався з меншою сумою, — безкоштовний', '1000')}
          {num('deposit_fee_pct', 'Комісія на ввід коштів, %', 'разово від суми; клієнт може вимкнути галочкою')}
          {num('tax_pct', 'Податки, %', 'щороку 25 липня з прибутку за минулий рік; клієнт може вимкнути')}
        </div>
      </div>

      <div style={C.card}>
        <div style={C.title}>Підказки «?» калькулятора{isUnsaved('help') && <UnsavedPill />}</div>
        <div style={C.sub}>
          У тексті можна вставити числа з налаштувань вище — вони підставляться на сайті:
          <code> {'{fee_tiers}'}</code> (тарифи списком), <code>{'{fee_pct}'}</code> (діапазон ставок, «15–35»),
          {' '}<code>{'{free_months}'}</code>, <code>{'{threshold}'}</code>, <code>{'{deposit_fee_pct}'}</code>, <code>{'{tax_pct}'}</code>.
        </div>
        {CALC_HELP.map(h => (
          <div key={h.key} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={C.label}>{h.label}</span>
              <button type="button" style={{ ...C.tool, fontSize: 12 }}
                onClick={() => {
                  setSaved(false);
                  setHelp(x => ({ ...x, [h.key]: h.html }));
                  setResetKeys(k => ({ ...k, [h.key]: (k[h.key] || 0) + 1 }));
                }}>Повернути текст за замовчуванням</button>
            </div>
            <RichEditor value={help[h.key]} resetKey={resetKeys[h.key]}
              onChange={v => { setSaved(false); setHelp(x => ({ ...x, [h.key]: v })); }} />
          </div>
        ))}
      </div>

      <div style={C.card}>
        <div style={C.title}>Сторінки сайту: title і description{isUnsaved('pages') && <UnsavedPill />}</div>
        <div style={C.sub}>
          Те, що бачать пошуковики й вкладка браузера. Порожнє поле — текст за замовчуванням
          (показано сірим).
        </div>
        {SITE_PAGES.map(p => {
          const v = pages[p.path] || {};
          return (
            <div key={p.path} style={C.page}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                {p.label} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>{p.path}</span>
              </div>
              <label style={C.label}>Title</label>
              <input style={C.input} value={v.title || ''} placeholder={p.title} maxLength={TITLE_MAX}
                onChange={e => setPage(p.path, 'title', e.target.value)} />
              <div style={C.count(false)}>{(v.title || '').length}/{TITLE_MAX}</div>
              <label style={C.label}>Description</label>
              <textarea style={{ ...C.input, resize: 'vertical' }} rows={2} value={v.description || ''}
                placeholder={p.description} maxLength={DESC_MAX}
                onChange={e => setPage(p.path, 'description', e.target.value)} />
              <div style={C.count((v.description || '').length > 160)}>
                {(v.description || '').length}/{DESC_MAX}{(v.description || '').length > 160 ? ' — Google покаже ~160 символів' : ''}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', position: 'sticky', bottom: 0,
                    background: dirty ? '#FFF7ED' : '#F9FAFB', padding: '12px',
                    borderTop: `1px solid ${dirty ? '#FDBA74' : '#E5E7EB'}`, borderRadius: 8 }}>
        <button style={C.btn(true)} onClick={save} disabled={saving}>
          {saving ? 'Збереження…' : (!dirty && saved) ? '✓ Збережено' : 'Зберегти'}
        </button>
        {dirty && !saving && (
          <span style={{ color: '#C2410C', fontSize: 13, fontWeight: 600 }}>
            ● Є незбережені зміни: {changedParts.map(p => PART_LABEL[p]).join(', ')}
          </span>
        )}
        {error && <span style={{ color: '#B91C1C', fontSize: 13 }}>{error}</span>}
      </div>
    </div>
  );
}
